import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { ApiService } from './service/api.service';
import { delay, mergeMap, repeat } from 'rxjs/operators';
import { IEnrollMFAPayload, IFriendlyNamePayload, IMFAData, ITenantInfo, IMFAInitiatePayload } from './model/IMfa.model';

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  constructor(private _apiService: ApiService) {}

  /**
   *
   * @returns boolean
   *
   * will return true for mobile devices else false
   */
  _isMobile = (): Observable<boolean> => {
    return of(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent));
  };

  /**
   *
   * @param status IMFAData is holds the initiate enrollment response
   * @param tenantInfo ITenantInfo is holds the tenant and client related informations
   * @param type string is holds the enrollment method
   * @returns string (value that used to construct the QR code)
   *
   * Will use this method to construct the QR code value
   */
  _constructQRData = (status: IMFAData, tenantInfo: ITenantInfo, type: string): string => {
    let url = `otpauth://totp/${tenantInfo.tenant_name ? tenantInfo.tenant_name : '-'}:-?t=${type}&d=-&issuer=${
      tenantInfo.tenant_name ? tenantInfo.tenant_name : '-'
    }&tk=${tenantInfo.tenant_key ? tenantInfo.tenant_key : '-'}&l=${tenantInfo.logo_uri ? tenantInfo.logo_uri : '-'}&cid=${
      status.authenticator_client_id ? status.authenticator_client_id : '-'
    }&burl=${tenantInfo.base_url ? tenantInfo.base_url : '-'}&eid=${
      status.exchange_id?.exchange_id ? status.exchange_id?.exchange_id : '-'
    }`;

    // check for the totp_secret available or not, if its there will update that into the url
    if(status.totp_secret){
      url += `&secret=${status.totp_secret}`
    }

    return url;
  };

  /**
   *
   * @param status IMFAData is holds the initiate enrollment response
   * @param tenantInfo ITenantInfo is holds the tenant and client related informations
   * @param type string is holds the enrollment method
   * @returns string Authenticator app query string
   *
   * Will use to construct the authenticator app query string that used to open the app from the UI
   */
  _constructAppQuery = (status: IMFAData, tenantInfo: ITenantInfo, type: string): string => {
   let url = `cidaas://t=${type}&d=-&issuer=${tenantInfo.tenant_name ? tenantInfo.tenant_name : '-'}&tk=${
      tenantInfo.tenant_key ? tenantInfo.tenant_key : '-'
    }&l=${tenantInfo.logo_uri ? tenantInfo.logo_uri : '-'}&cid=${
      status.authenticator_client_id ? status.authenticator_client_id : '-'
    }&burl=${tenantInfo.base_url ? tenantInfo.base_url : '-'}&eid=${
      status.exchange_id?.exchange_id ? status.exchange_id?.exchange_id : '-'
    }`;

    // check for the totp_secret available or not, if its there will update that into the url
    if(status.totp_secret){
      url += `&secret=${status.totp_secret}`
    }

    return url;
  };

  /**
   *
   * @param url string
   * Will use to open the Authenticator app url
   */
  _openAuthenticatorApp = (url: string) => {
    window.location.href = url;
  };

/**
 *
 * @param payload  IFriendlyNamePayload is holds the payload for the updating the friendly name
 * @param track_Id  string
 * @param method  string
 * @returns Observable
 *
 * Will save the friendly name and return an observable of success or error
 */
  _saveFriendlyName = (payload:IFriendlyNamePayload, track_Id:string, method:string) => {
    return this._apiService._configureFriendlyName(payload,track_Id, method);
  };

  /**
   *
   * @param trackId string
   * @param method string type of verification
   * @returns Observable
   *
   * Will use this method to initiate the enrollment
   */
  _initiateSuggestVerification = (trackId: string, method: string, payload?:IMFAInitiatePayload) => {
    return this._apiService._initiateVerification(trackId, method, payload).pipe(mergeMap((res)=>!res.success || !res.data ? throwError({message:`Initiate ${method} enrolment process got failed`, status:res.status}) : of(res?.data)));
  };

  /**
   *
   * @param payload IEnrollMFAPayload
   * @param method string type of verification
   * @returns Observable
   *
   * Will use this method to save the enrollment
   */
  _configureSuggestVerification = (payload:IEnrollMFAPayload, method:string) =>{
    return this._apiService._configureVerification(payload, method).pipe(mergeMap((res)=>!res.success || !res.data ? throwError({message:`Configuration of ${method} got failed`, status:res.status}) : of(res?.data)));;
  }

  /**
   *
   * @param status_id
   * @returns Observable
   *
   * will trigger the api in 3s to listen the socket status from the authenticator app
   * possible status are SCANNED,CANCELLED and ENROLLED
   */
  _listenSocketStatus = (status_id: string) => {
    return this._apiService._listenSocket(status_id).pipe(delay(1000), repeat());
  };

  _userActionOnEnrolment = (track_id:string, action:string) =>{
    return this._apiService._userActionOnEnrolment(track_id, action).pipe(mergeMap((res)=>!res.success || !res.data ? throwError({message:`ERROR_CODES.SUGGEST_VERIFICATION.USER_ACTION.${res.code}`, default_message:`ERROR_CODES.SUGGEST_VERIFICATION.USER_ACTION.DEFAULT`, status:res.status}) : of(res?.data)));
  };

}
